
import Vue, { PropOptions } from 'vue';
import _ from 'lodash';
import axios from 'axios';
import { QtItemsTable } from '@/models/page-models/page-items/QtItemsTable';
import { productAlias } from '@/helpers/productAlias';
import { baseUnit } from '@/helpers/baseUnit';
import { currency } from '@/helpers/currency';
import { unitPriceFormat } from '@/helpers/numFormat';
import { dateFormat } from '@/helpers/dateFormat';
import { mergeArray } from '@/helpers/mergeArray';


export default Vue.extend({
  props: {
    input: { type: Object, default: () => {} }  as PropOptions<QtItemsTable>,
    data: { type: Object, default: () => {} },
    getVariable: { type: Function, default: () => () => {} },
    callFunction: { type: Function, default: () => () => {} },
    onChange: { type: Function, default: () => {} },
    parent: { type: Object, default: () => {} },
    rowDisabled: { type: Boolean, default: false }
  },
  data(): {
    tableVariable: Array<any>,
    renderFlag: boolean,
    priceDialog: {
      showDialog: boolean,
      price: number | string,
      minimumQuantity: number | string,
      tableVariable: any,
      itemPriceBracketArray: any[]
    },
    sellingUnit: string,
    priceHistory: any[],
    footerTotals: { value: number, vatValue: number, total: number }
  } {
    return {
      tableVariable: [],
      renderFlag: true,
      priceDialog: {
        showDialog: false,
        price: '',
        minimumQuantity: '',
        tableVariable: {},
        itemPriceBracketArray: []
      },
      sellingUnit: '',
      priceHistory: [],
      footerTotals: { value: 0, vatValue: 0, total: 0 }
    };
  },
  computed: {
    tableDisabled: function(): boolean {
      return this.rowDisabled || this.parent._getDisableStatus(this.input.disabled);
    },
    tableItems: function (): any[] {
      const tableItemsSource: any[] = this.parent._getVariable(this.input.items) || [];
      const currentItemsSource: any[] = (this.parent._getVariable(this.input.currentItemsVariable) || []).map((item: any) => item.product);
      const mergedSource = mergeArray(tableItemsSource, currentItemsSource, '_id');
      const items: any[] = Array.isArray(this.tableVariable) ? this.tableVariable.map((formDataRow: any, __key: Number) => {
        const foundItem = mergedSource.find(
          (sourceItem: any) =>  _.get(sourceItem, this.input.itemKey) === _.get(formDataRow, this.input.variableKey)
        );
        const itemKey = _.get(foundItem, this.input.itemKey, __key);
        return {... foundItem, __key, itemKey };
      }) : [];
      return items;
    },
    tableHeaders: function(): any[] {
      let tableHeaders = [
        {
          "value": "__key",
          "text": "Sl. No"
        },
        {
          "value": "_id",
          "text": "Item ID"
        },
        {
          "value": "alias",
          "text": "Name"
        },
        {
          "value": "repeatType",
          "text": "Type"
        },
        {
          "value": "sellingUnit",
          "text": "Unit"
        },
        {
          "value": "unitPrice",
          "text": "Unit Price"
        },
        {
          "value": "totalQuantity",
          "text": "Quantity",
          "width": "150px"
        },
        {
          "value": "value",
          "text": "Sub Total"
        },
        {
          "value": "vatRate",
          "text": "VAT %"
        },
        {
          "value": "vatValue",
          "text": "VAT Value"
        },
        {
          "value": "total",
          "text": "Total"
        }
      ];
      if(this.tableDisabled || this.parent.role.includes('Accounting | Costing')) return tableHeaders;
      if(this.parent._resolveFormulaValue(`fn(LOGICAL,fn(CONCAT,',{{formData.status}},'),==,'Pending Revision')`)
        &&
        (!this.priceBracketOnly || this.tableItems.length < 2)
      )  return tableHeaders;
      if(this.parent._resolveFormulaValue(`fn(LOGICAL,fn(CONCAT,',{{formData.status}},'),!=,'Open')`)) {
        tableHeaders.push({ "value": "actions", "text": "Actions" });
      }
      return tableHeaders;
    },
    vatPercent: function(): number {
      return this.parent._getVariable('fetchedData.vatPercent');
    },
    priceBracketOnly: function(): boolean {
      return this.parent._getVariable(this.input.priceBracketVariable) || false;
    },
    enablePriceChange: function(): boolean {
      return !this.parent.role.includes('Sales | Account Executive');
    },
    priceHistoryHeaders: function(): { value: string, text: string }[] {
      return [
        {
          "value": "updatedAt",
          "text": "Date"
        },
        {
          "value": "price",
          "text": "Price in SAR"
        },
        {
          "value": "minimumQuantity",
          "text": "Minimum Quantity"
        },
        {
          "value": "quantity",
          "text": "Last Order Quantity"
        }
      ];
    }
  },
  mounted() {
    this.$watch(
      () => this.parent._getVariable(this.input.variable), 
      (tableVariable) => {
        if (tableVariable) {
          // link variable with store
          this.tableVariable = tableVariable;
          this.updateFooterTotals();
        }
      },
      {
        immediate: true, deep: true
      }
    )
  },
  methods: {
    async forceUpdate() {
      this.renderFlag = false;
      await this.$nextTick();
      this.renderFlag = true;
      this.updateFooterTotals();
    },
    updateFooterTotals() {
      const value = this.tableVariable.reduce((totalValue, currentVariable) => {
        return totalValue + _.get(currentVariable, 'productPrice.price', 0) * (currentVariable.totalQuantity || 0);
      }, 0);
      const vatValue = value * this.vatPercent * 0.01;
      const total = value + vatValue;
      this.footerTotals = { value, vatValue, total };
    },
    getProductAlias(product: any): string {
      return productAlias(product);
    },
    getBaseUnit(product: any): string {
      return baseUnit(product);
    },
    getUnitPriceFormat(price: number): string | number {
      return unitPriceFormat(price);
    },
    getCurrency(value: any): string | number {
      return currency(value);
    },
    getFormattedDate(date: string): string {
      return dateFormat(date);
    },
    
    onDelete(targetItem: any) {
      const index = this.tableVariable.indexOf(targetItem);
      if (confirm('Are you sure you want to delete this item?')) {
        this.tableVariable.splice(index, 1);
        if(this.input.executeOnDelete) {
          this.parent._callMultipleFunctions(this.input.executeOnDelete);
        }
      }
    },
    getSubTotal(tableVariable: any): number {
      return _.get(tableVariable, 'productPrice.price', 0) * _.get(tableVariable, 'totalQuantity', 0);
    },
    getFormattedDropdownValues(item: any): { text: string, value: string }[] {
      const itemPriceBracketArray = item.priceBracket;
      const baseUnit = _.get(item, 'unit.unit2', 'unit(s)');
      if(!itemPriceBracketArray) return [];
      return itemPriceBracketArray.filter((productPrice: any) => productPrice.status != 'Rejected').map((productPrice: any) => {
        let text = `${unitPriceFormat(productPrice.price)} (${productPrice.minimumQuantity} ${baseUnit})`;
        if(productPrice.status == 'Pending') text = `${text} [Pending]`;
        const value = productPrice._id;
        return { text, value };
      });
    },
    async openDialog(itemPriceBracketArray: any[], tableVariable: any, sellingUnit: string) {
      this.sellingUnit = sellingUnit;
      await this.setPriceHistory(tableVariable.product);
      this.priceDialog.itemPriceBracketArray = itemPriceBracketArray;
      this.priceDialog.tableVariable = tableVariable;
      if(tableVariable.totalQuantity) {
        this.priceDialog.minimumQuantity = tableVariable.totalQuantity;
      }
      this.priceDialog.showDialog = true;

    },
    async setPriceHistory(productId: number) {
      try {
        const response = (await axios.get(`/api/v1/product/${productId}/price-history`)).data;
        this.priceHistory = _.get(response, 'data', []);
      } catch (error) {
        this.$store.commit('setSnackbar', { serverMessage: error.response.data.message || error.message, show: true, color: 'error' });
      }
    },
    resetDialogParams() {
      this.priceDialog = {
        showDialog: false,
        price: '',
        minimumQuantity: '',
        tableVariable: {},
        itemPriceBracketArray: []
      };
    },
    addPrice() {
      const { price, minimumQuantity } = this.priceDialog;
      this.priceDialog.itemPriceBracketArray.push({ _id: `new_${price}_${minimumQuantity}`, price, minimumQuantity, status: 'Pending' });
      this.priceDialog.tableVariable.productPrice = { _id: `new_${price}_${minimumQuantity}`, price, minimumQuantity, status: 'Pending' };
      this.resetDialogParams();
    },
    changePrice(itemPriceBracketArray: any[], tableVariable: any) {
      const selectedProductPrice = itemPriceBracketArray.find((productPrice: any) => productPrice._id == tableVariable.productPrice._id);
      tableVariable.productPrice = selectedProductPrice;
      const minimumQuantity = tableVariable.productPrice.minimumQuantity;
      if(!tableVariable.totalQuantity || tableVariable.totalQuantity < minimumQuantity) {
        tableVariable.totalQuantity = minimumQuantity;
      }
    },
    handlePriceSelection(selectedId: number, item: any) {
      const currentItem = this.tableVariable[item.__key];
      if (!currentItem.productPrice) {
        this.$set(currentItem, 'productPrice', {});
      }
      currentItem.productPrice._id = selectedId;
      this.changePrice(item.priceBracket, currentItem);
    }
  }
});
